import { MsgFragmentNewChild } from "../Chats/MsgFragmentNewChild";
import { __debug } from "../Debug/Debug";
import { IMsgFragmentNewChild, IPendingTask } from "../Interfaces";
import { Timer } from "../Timer/Timer";


/**
 * CallbackManagerTimer class
 */
export class CallbackManagerTimerEx {

    /**
     * name of the callback
     */
    private name: string = "";
    /**
     * this class supports only one callback to registered at a time.
     * If new callback is registered the previous one will be destroyed
     * and will neved get called.
     */
    private callback: any;
    /**
     * stores the state on the empty callback
     */
    private calledEmpty: boolean =  false;
    /**
     * stores the empty callback that is not called yet 
     */
    private unCalledEmptyCallback!: Function;
    /**
     * warnings
     */
    private warnings: boolean = true;
    /**
     * triggers empty callback if message fragment is not valid
     * see the example below:
     * ==========================================================
     * Example code:
     * __callbacks.msgFragmentEx.trigger(...)
     * .onEmpty(() => { })   <=======================  onEmpty will get triggerd if the message fragment is not valid
     * ==========================================================
     */
    private __emptyCallback: any = {
        onEmpty: (callback: Function) => {
            // mark that the empty callback has been called
            this.calledEmpty = true;
            callback();
        }
    }
    /**
     * saves empty callback if message fragment is not valid
     * see the example below:
     * ==========================================================
     * Example code:
     * __callbacks.msgFragmentEx.trigger(...)
     * .onEmpty(() => { })   <=======================  onEmpty will not get triggerd. it will get triggered as  [this.unCalledEmptyCallback()]
     * ==========================================================
     */
    private __emptyCallbackSingnature = {
        onEmpty: (callback: Function) => {
            // save the callback to be triggered at later in time
            this.unCalledEmptyCallback = callback;
        }
    }
    /**
     * callback Timer
     */
    private timer: Timer | undefined;
    /**
     * callback timout
     */
    private timout = 86400; // on day expire time by default
    /**
     * pending tasks to be resolved
     */
    private pendingTasks: IPendingTask = { };

    private lastMessage: IMsgFragmentNewChild | undefined;


    /**
     * Manages callback functions
     * @param {string} name name
     * @param {number=} [timout=86400] [86400ms] on day expire time by default
     * @param {boolean=} warnings warnings
     * 
     */
    constructor(name: string, timout: number, warnings: boolean = true) {
        this.name = name;
        this.timout = timout;
        this.warnings = warnings;
    }
    /**
     * registers a callback
     * @param {Function} callback
     * @returns {void} returns nothing
     */
    register = (callback: Function, messageNo: number) => {
        // reslove pending tasks before registering the callback
        this.resolvePendingTasks(callback, messageNo);
        // destroy the previously registered callback
        this.destroyCallback();
        // now register the callback
        this.callback = callback;
        // set the timer to destroy the registered callback after the given time
        // the time is given by (timout) parameter in the constructor
        this.initNewTimer();
    }
    /**
     * triggers the callbacks registered via the (registerCallback) method
     * @param {any} data
     * @returns {void} returns nothing
     */
    trigger = (data: MsgFragmentNewChild) => {

        // check if the message should be a fragment
        const isPreviousUser: boolean = (this.lastMessage?.userId === data.get().userId);
        const shouldBeFragment: boolean = isPreviousUser && data.get().fragment;
        // set the last message for later use
        this.lastMessage = data.get();

        // check if it is not a msg fragment
        // then call the empty callback
        if(!shouldBeFragment) return this.__emptyCallback;

        // got a msg fragement
        if(this.calledEmpty) {
            this.addPendingTask(data);
            return this.__emptyCallbackSingnature;
        }
        // check if no callbacks registered
        if (this.isEmpty()) {
            if (this.warnings) {
                __debug.warn(() => {
                    console.warn(`No callbacks registered for: [${this.name}]`);
                });
            }
            return this.__emptyCallback;
        }
        // trigger callbacks
        this.callback(data);
        // reset the timer
        this.clearTimer();
        this.initNewTimer();
        return this.__emptyCallbackSingnature;
    }
    /**
     * determines if the callback exists and is a function
     * @param callback any
     * @returns {boolean}
     */
    isCallback = (callback: any) => {
        return (typeof callback === "function");
    }
    /**
     * determines if there are no callbacks registered.
     * @returns {boolean} true or false
     */
    isEmpty = () => {
        return !this.isCallback(this.callback);
    }
    /**
     * destroys the registered callback
     */
    destroyCallback = () => {
        __debug.warn(() => {
            console.warn(`destroyCallback ${this.name}`);
        });
        this.callback = null;
        this.clearTimer();
        this.calledEmpty = false;
    }
    /**
     * destroys the registered callback and calls empty callback
     */
    public destroyAndCallEmpty = () => {
        __debug.warn(() => {
            console.warn("destroyAndCallEmpty");
        });
        //this.callEmptyOnDestroy = true;
        if(this.unCalledEmptyCallback) this.unCalledEmptyCallback();
    }
    /**
     * initializes a new timer
     * after the given time it will destroy the registered callback
     */
    initNewTimer = () => {
        this.timer = new Timer({
            timeout: this.timout,
            callback: () => {
                this.destroyCallback();
            }
        })
    }
    /**
     * clears the timer
     */
    clearTimer = () => {
        this.timer?.clear();
    }
    /**
     * makes the data as a pending task
     * @param data MsgFragmentNewChild
     */
    addPendingTask = (data: MsgFragmentNewChild) => {
        var task = this.pendingTasks[data.get().messageNo];
        if(!task) task = this.pendingTasks[data.get().messageNo] = [];
        task.push(data);
    }
    /**
     * resolves the pending tasks
     * @param callback Function
     * @param messageNo number
     */
    resolvePendingTasks = (callback: Function, messageNo: number) => {
        this.pendingTasks[messageNo+1]?.forEach(callbackData => {
            callback(callbackData);
        })
        this.emptyPendingTasks(messageNo+1);
    }
    /**
     * empties pending tasks
     * @param messageNo number
     */
    emptyPendingTasks = (messageNo: number) => {
        this.pendingTasks[messageNo] = [];
    }
}

// test data
// const testCallback = new CallbackManagerTimer("testCallback", false);

// const onTestCallback = (callback) => {
//     testCallback.register(callback);
// }

// onTestCallback((data) => {
//     console.log(data);
// });

// testCallback.trigger({ data: "the data - 1" })
//     .onEmpty(() => {
//         console.log("on empty - 1");
//     });

// setTimeout(() => {
//     testCallback.trigger({ data: "the data - 2" })
//         .onEmpty(() => {
//             console.log("on empty - 2");
//         });
// }, 2000);

// setTimeout(() => {
//     testCallback.trigger({ data: "the data - 3" })
//         .onEmpty(() => {
//             console.log("on empty - 3");
//         });
// }, 4000);

// setTimeout(() => {
//     testCallback.trigger({ data: "the data - 4" })
//         .onEmpty(() => {
//             console.log("on empty - 4");
//         });
// }, 6000);

// setTimeout(() => {
//     testCallback.trigger({ data: "the data - 5" })
//         .onEmpty(() => {
//             console.log("on empty - 5");
//         });
// }, 10000);

